import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faEdit,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import MiniLoader from "./MiniLoader";

const FileUploader = ({
  previewUrl,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  imageDragOver,
  accept = "image/*",
  placeholder = "اسحب وأفلت الملف هنا",
  subText = "أو اضغط لاختيار ملف",
  buttonText = "اختيار ملف",
  changeButtonText = "تغيير الملف",
  className = "",
  id = "file-upload",
  MaxFileSize = 10, // in MB
  BackgroundColor = "#f9fafb",
  placeholderColor = "#374151",
  subTextColor = "#6b7280",
  hoverBackgroundColor = "#f0f4ff",
  fileName = null,
  fileNameColor = "#374151",
  useMiniLoading = false,
  isVideo = false,
  uploadMultipleFile = false,
}) => {
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [localFiles, setLocalFiles] = React.useState([]);

  // Handle file change with size validation
  const handleFileChangeWithValidation = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      if (uploadMultipleFile) {
        let isValid = true;
        for (const file of files) {
          const fileSizeInMB = file.size / (1024 * 1024);
          if (fileSizeInMB > MaxFileSize) {
            setErrorMessage(`حجم الملف ${file.name} كبير جداً. الحد الأقصى المسموح به هو ${MaxFileSize} ميجابايت`);
            isValid = false;
            break;
          }
        }
        if (!isValid) {
          e.target.value = "";
          return;
        }
        setErrorMessage(null);
        setLocalFiles(files);
      } else {
        const file = files[0];
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > MaxFileSize) {
          setErrorMessage(`حجم الملف كبير جداً. الحد الأقصى المسموح به هو ${MaxFileSize} ميجابايت`);
          e.target.value = ""; // Reset input
          return;
        }
        setErrorMessage(null);
        setLocalFiles([file]);
      }
    }
    if (onFileChange) onFileChange(e);
  };

  const handleDrop = (e) => {
    if (uploadMultipleFile) {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) {
        let isValid = true;
        for (const file of files) {
          const fileSizeInMB = file.size / (1024 * 1024);
          if (fileSizeInMB > MaxFileSize) {
            setErrorMessage(`حجم الملف ${file.name} كبير جداً. الحد الأقصى المسموح به هو ${MaxFileSize} ميجابايت`);
            isValid = false;
            break;
          }
        }
        if (isValid) {
          setErrorMessage(null);
          setLocalFiles(files);
          if (onFileChange) {
            onFileChange({ target: { files: e.dataTransfer.files }, currentTarget: { files: e.dataTransfer.files } });
          }
        }
      }
      if (onDragLeave) onDragLeave(e);
      if (onDrop) onDrop(e);
    } else {
      if (onDrop) onDrop(e);
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById(id);
    if (fileInput) {
      fileInput.value = "";
      fileInput.click();
    }
  };

  const handleRemoveFile = (e, indexToRemove) => {
    e.stopPropagation();
    const newFiles = localFiles.filter((_, idx) => idx !== indexToRemove);
    setLocalFiles(newFiles);

    if (onFileChange) {
      const dt = new DataTransfer();
      newFiles.forEach(file => dt.items.add(file));
      onFileChange({ target: { files: dt.files }, currentTarget: { files: dt.files } });
    }
  };

  return (
    <div className={`file-uploader ${className}`}>
      <div
        className={`upload-area ${imageDragOver ? "drag-over" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
      >
        {uploadMultipleFile && localFiles.length > 0 ? (
          <div className="file-preview-container multiple" style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="multiple-files-grid">
              {localFiles.map((file, idx) => {
                const url = URL.createObjectURL(file);
                return (
                  <div key={idx} className="multiple-file-item" onClick={(e) => { e.stopPropagation(); window.open(url, "_blank"); }}>
                    <button 
                      type="button" 
                      className="remove-file-btn" 
                      onClick={(e) => handleRemoveFile(e, idx)}
                      title="إزالة الملف"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <FontAwesomeIcon icon={faUpload} className="item-icon" />
                    <span className="item-name" title={file.name}>{file.name}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="change-file-btn"
                style={{ position: 'static', opacity: 1, background: '#e0e7ff', color: '#3730a3' }}
                onClick={(e) => {
                  e.stopPropagation();
                  triggerFileInput();
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                {changeButtonText}
              </button>
            </div>
          </div>
        ) : previewUrl ? (
          <div className="file-preview-container">
            {previewUrl === "pdf-placeholder" ? (
              <div className="pdf-placeholder">
                <div className="pdf-icon">
                  <FontAwesomeIcon icon={faUpload} />
                </div>
                {fileName && <p className="file-name">{fileName}</p>}
              </div>
            ) : isVideo || previewUrl?.toString().match(/\.(mp4|webm|ogg|mov)$/i) ? (
              <video
                src={previewUrl}
                className="preview-file"
                controls
                muted
              />
            ) : (
              <img
                src={previewUrl}
                alt="File preview"
                className="preview-file"
              />
            )}
            <div className="file-overlay">
              <button
                type="button"
                className="change-file-btn"
                onClick={triggerFileInput}
              >
                <FontAwesomeIcon icon={faEdit} />
                {changeButtonText}
              </button>
            </div>
          </div>
        ) : (
          <div className="upload-placeholder">
            {useMiniLoading ? (
              <div style={{ marginBottom: '10px' }}>
                <MiniLoader size={40} color="#667eea" variant="dots" />
              </div>
            ) : (
              <div className="upload-icon">
                <FontAwesomeIcon icon={faUpload} />
              </div>
            )}
            <h4>{placeholder}</h4>
            <p>{subText}</p>
            <button
              type="button"
              className="select-file-btn"
              onClick={() => !useMiniLoading && triggerFileInput()}
              disabled={useMiniLoading}
              style={{ opacity: useMiniLoading ? 0.7 : 1, cursor: useMiniLoading ? "not-allowed" : "pointer" }}
            >
              {useMiniLoading ? (
                <>
                  <MiniLoader size={16} color="#ffffff" variant="dots" />
                  <span style={{ marginRight: '8px' }}>{buttonText}</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} />
                  {buttonText}
                </>
              )}
            </button>
          </div>
        )}
        <input
          type="file"
          id={id}
          accept={accept}
          multiple={uploadMultipleFile}
          onChange={handleFileChangeWithValidation}
          style={{ display: "none" }}
        />
      </div>

      {errorMessage && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {errorMessage}
        </div>
      )}

      <style jsx>{`
        .file-uploader {
          width: 100%;
        }

        .multiple-files-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 10px;
          width: 100%;
          max-height: 250px;
          overflow-y: auto;
        }

        .multiple-file-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px 10px 10px;
          background: #f8fafc;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
          position: relative;
        }

        .remove-file-btn {
          position: absolute;
          top: 6px;
          left: 6px;
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 2;
        }

        .remove-file-btn:hover {
          background: #ef4444;
          color: white;
          transform: scale(1.1);
        }

        .multiple-file-item:hover {
          background: #f1f5f9;
          border-color: #667eea;
        }

        .item-icon {
          font-size: 24px;
          color: #667eea;
          margin-bottom: 8px;
          margin-top: 4px;
        }

        .item-name {
          font-size: 12px;
          color: #374151;
          text-align: center;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .file-preview-container.multiple {
          background: transparent;
          box-shadow: none;
        }

        .upload-area {
          border: 2px dashed #d1d5db;
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          background: ${BackgroundColor};
          cursor: pointer;
          width: 100%;
        }

        .upload-area:hover,
        .upload-area.drag-over {
          border-color: #667eea;
          background: ${hoverBackgroundColor};
        }

        .upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .upload-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .upload-placeholder h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: ${placeholderColor};
          margin: 0;
        }

        .upload-placeholder p {
          color: ${subTextColor};
          margin: 0;
          font-size: 0.9rem;
        }

        .select-file-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          font-family: inherit;
        }

        .select-file-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .file-preview-container {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .preview-file {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
        }

        .pdf-placeholder {
          width: 100%;
          height: 200px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(102, 126, 234, 0.1);
          gap: 1rem;
        }

        .pdf-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 2rem;
        }

        .file-name {
          color: ${fileNameColor};
          font-weight: 600;
          font-size: 0.95rem;
          margin: 0;
          padding: 0 1rem;
          text-align: center;
          word-break: break-word;
        }

        .file-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .file-preview-container:hover .file-overlay {
          opacity: 1;
        }

        .change-file-btn {
          background: rgba(255, 255, 255, 0.9);
          color: #1f2937;
          border: none;
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .change-file-btn:hover {
          background: white;
          transform: scale(1.05);
        }

        .error-message {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          background: #e4bfbfff;
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #dc2626;
          font-size: 0.9rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          animation: slideDown 0.3s ease;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .upload-area {
            padding: 1rem;
          }

          .upload-placeholder h4 {
            font-size: 1rem;
          }

          .upload-placeholder p {
            font-size: 0.85rem;
          }

          .select-file-btn,
          .change-file-btn {
            padding: 0.625rem 1.25rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .upload-area {
            padding: 0.75rem;
          }

          .upload-icon {
            width: 50px;
            height: 50px;
            font-size: 1.25rem;
          }

          .upload-placeholder h4 {
            font-size: 0.95rem;
          }

          .upload-placeholder p {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FileUploader; 